"use client";

import * as React from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";

import { useTranslations } from "@/i18n";
import { posthog } from "@/instrument";
import { AppErrorScreen, type AppErrorScreenLabels } from "@ibada/ui/components/app-error-screen";

/**
 * Class wrapper that catches React tree errors and renders the **single**
 * canonical `AppErrorScreen` (from `@ibada/ui`). Do not duplicate the
 * presentation here — extend `AppErrorScreen` in `packages/ui` if you
 * need a variant.
 */
type AppErrorBoundaryCardProps = {
  resetKey: string;
  labels: AppErrorScreenLabels;
  pathname: string;
  onReload: () => void;
  onHome: () => void;
  onCopySuccess: () => void;
  onCopyError: () => void;
  children: React.ReactNode;
};

class AppErrorBoundaryCard extends React.Component<
  AppErrorBoundaryCardProps,
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    posthog?.captureException(error, {
      extra: {
        componentStack: info.componentStack,
        pathname: this.props.pathname,
        source: "tenant_react_error_boundary",
      },
    });
  }

  componentDidUpdate(prevProps: Readonly<AppErrorBoundaryCardProps>) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <AppErrorScreen
          error={this.state.error}
          pathname={this.props.pathname}
          labels={this.props.labels}
          showStackTrace={import.meta.env.DEV}
          onReload={this.props.onReload}
          onHome={this.props.onHome}
          onCopySuccess={this.props.onCopySuccess}
          onCopyError={this.props.onCopyError}
        />
      );
    }

    return this.props.children;
  }
}

export function AppErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const t = useTranslations("errorBoundary");

  const labels: AppErrorScreenLabels = {
    eyebrow: t("eyebrow"),
    title: t("title"),
    description: t("description"),
    reloadLabel: t("actions.reload"),
    homeLabel: t("actions.home"),
    detailsLabel: t("details"),
    routeLabel: t("route"),
    messageLabel: t("message"),
    copyLabel: t("actions.copy"),
    copiedLabel: t("actions.copied"),
  };

  const handleReload = React.useCallback(() => {
    window.location.reload();
  }, []);

  /** `/dashboard` delegates recovery to the server-owned last-context resolver. */
  const handleHome = React.useCallback(() => {
    window.location.assign("/dashboard");
  }, []);

  const copySuccessLabel = t("copySuccess");
  const copyErrorLabel = t("copyError");
  const handleCopySuccess = React.useCallback(() => {
    toast.success(copySuccessLabel);
  }, [copySuccessLabel]);
  const handleCopyError = React.useCallback(() => {
    toast.error(copyErrorLabel);
  }, [copyErrorLabel]);

  return (
    <AppErrorBoundaryCard
      resetKey={location.key}
      pathname={location.pathname}
      labels={labels}
      onReload={handleReload}
      onHome={handleHome}
      onCopySuccess={handleCopySuccess}
      onCopyError={handleCopyError}
    >
      {children}
    </AppErrorBoundaryCard>
  );
}
